import { Grid, Stack } from '@careflux/primitives';
import { HorizontalBarChart } from './HorizontalBarChart';
import { MetricTile } from './MetricTile';
import { QueryState } from './QueryState';
import { TrendChart } from './TrendChart';
import type { TabProps } from './components.types';
import { UNKNOWN_LABEL, formatLatency, formatPercent } from './formatting';
import type { BreakdownRow } from './types';
import { useSummaryBreakdown, useSummaryMetrics, useSummaryTimeline } from './useSummaryQualityQueries';

// p95 is the second series; its coral step is re-validated for the dark surface.
const LATENCY_SERIES = [
  { dataKey: 'latencyP50Ms', label: 'p50', color: 'hsl(var(--chart-1))' },
  { dataKey: 'latencyP95Ms', label: 'p95', color: 'hsl(var(--chart-2))', darkColor: '#DD6547' },
] as const;

const p95ByModel = (rows: BreakdownRow[]) =>
  rows
    .filter((row): row is BreakdownRow & { latencyP95Ms: number } => row.latencyP95Ms != null)
    .map((row) => ({ label: row.label || UNKNOWN_LABEL, value: row.latencyP95Ms }));

export const LatencyTab = ({ filters, bucket, onBucketChange }: TabProps) => {
  const metricsQuery = useSummaryMetrics(filters);
  const timelineQuery = useSummaryTimeline(filters, bucket);
  const modelQuery = useSummaryBreakdown(filters, 'model');
  const metrics = metricsQuery.data;

  return (
    <Stack gap="lg">
      <QueryState
        isLoading={metricsQuery.isLoading}
        isError={metricsQuery.isError}
        error={metricsQuery.error}
        onRetry={() => void metricsQuery.refetch()}
        loadingLabel="Sæki biðtíma"
        errorTitle="Biðtími hlóðst ekki"
      />
      {metrics && (
        <Grid columns={{ base: 1, md: 3 }} gap="md">
          <MetricTile title="Biðtími p50" value={formatLatency(metrics.latencyP50Ms)} detail="Heildarbið notanda" />
          <MetricTile title="Biðtími p95" value={formatLatency(metrics.latencyP95Ms)} detail="Heildarbið notanda" />
          <MetricTile
            title="Varaleið notuð"
            value={formatPercent(metrics.fallbackRate)}
            detail="Hlutfall samantekta sem féllu á varalíkan"
          />
        </Grid>
      )}

      <QueryState
        isLoading={timelineQuery.isLoading}
        isError={timelineQuery.isError}
        error={timelineQuery.error}
        onRetry={() => void timelineQuery.refetch()}
        loadingLabel="Sæki tímalínu"
        errorTitle="Tímalína hlóðst ekki"
      />
      {timelineQuery.data && (
        <TrendChart
          title="Biðtími yfir tíma"
          description="p50 og p95 heildarbiðtími eftir samantekt"
          points={timelineQuery.data}
          series={[...LATENCY_SERIES]}
          formatValue={formatLatency}
          bucket={bucket}
          onBucketChange={onBucketChange}
        />
      )}

      <QueryState
        isLoading={modelQuery.isLoading}
        isError={modelQuery.isError}
        error={modelQuery.error}
        onRetry={() => void modelQuery.refetch()}
        loadingLabel="Sæki líkön"
        errorTitle="Líkön hlóðust ekki"
      />
      {modelQuery.data && (
        <HorizontalBarChart
          title="p95 biðtími eftir líkani"
          description="Líkanið sem svaraði að lokum"
          data={p95ByModel(modelQuery.data)}
          valueLabel="p95"
          formatValue={formatLatency}
        />
      )}
    </Stack>
  );
};
